import { useEffect, useRef } from "react";
import { Leaf, Syringe, Stethoscope, ExternalLink } from "lucide-react";
import glp1Patch from "../../assets/glp1-patch.png";
import fluShots from "../../assets/flu-shots.jpg";
import doctronicVisit from "../../assets/doctronic-visit.jpg";

const whatsapp = (msg) =>
  `https://wa.me/12016742138?text=${encodeURIComponent(`Hi EZ Care Pharmacy! ${msg}`)}`;

const cards = [
  {
    id: "glp1-patches",
    icon: Leaf,
    image: glp1Patch,
    alt: "Woman applying an all-natural GLP-1 patch to her upper arm",
    title: "All-Natural GLP-1 Patches",
    text: "A simple, all-natural way to support your wellness journey — no prescription necessary. A full month's supply for just $30.",
    primary: { label: "Call to Learn More", href: "tel:+18625201063" },
    secondary: {
      label: "WhatsApp Us",
      href: whatsapp("I would like to know more about the GLP-1 patches."),
      external: true,
    },
  },
  {
    id: "flu-shots",
    icon: Syringe,
    image: fluShots,
    alt: "Flucelvax influenza vaccine box — Flu shots are here!",
    imageClass: "object-cover",
    imageStyle: { objectPosition: "center 70%" },
    title: "Flu Shots Are Here!",
    text: "The 2026–2027 flu vaccine is now in stock. Protect yourself and your family this season — stop by or call us to get your flu shot.",
    primary: { label: "Call to Schedule", href: "tel:+18625201063" },
    secondary: {
      label: "WhatsApp Us",
      href: whatsapp("I would like to get a flu shot."),
      external: true,
    },
  },
  {
    id: "doctor-visit",
    icon: Stethoscope,
    image: doctronicVisit,
    alt: "Doctronic — No insurance? No problem. See a doctor for $39.",
    imageClass: "object-contain bg-[#fdfdf7]",
    imageLink: "https://www.doctronic.ai/no-insurance-doctor-visit/",
    title: "No Insurance? See a Doctor Online",
    text: "Don't have insurance? Chat with Doctronic's AI doctor for free, then have a video visit with a doctor for just $39 — no insurance required. If you're prescribed medication, send it to EZ Care Pharmacy and we'll have it ready for you.",
    primary: {
      label: "Book a Doctor Visit",
      href: "https://www.doctronic.ai/no-insurance-doctor-visit/",
      external: true,
    },
  },
];

const externalProps = { target: "_blank", rel: "noopener noreferrer" };

export default function Announcements() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="whats-new"
      data-testid="announcements-section"
      className="ez-section bg-gradient-to-br from-teal-50 to-white"
      ref={ref}
    >
      <div className="ez-container">
        <div className="text-center mb-12 animate-on-view">
          <div className="inline-block text-ez-teal font-semibold font-caveat text-lg mb-3">
            New &amp; Featured
          </div>
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-ez-text">
            Now Available at <span className="text-ez-teal">EZ Care Pharmacy</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map(({ id, icon: Icon, image, alt, imageClass = "object-cover", imageStyle, imageLink, title, text, primary, secondary }, i) => (
            <div
              key={id}
              id={id}
              data-testid={`${id}-card`}
              className="animate-on-view bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col"
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              {image ? (
                imageLink ? (
                  <a href={imageLink} {...externalProps}>
                    <img src={image} alt={alt} className={`w-full h-64 ${imageClass}`} style={imageStyle} />
                  </a>
                ) : (
                  <img src={image} alt={alt} className={`w-full h-64 ${imageClass}`} style={imageStyle} />
                )
              ) : (
                <div className="w-full h-64 bg-teal-50 flex items-center justify-center">
                  <Icon size={96} className="text-ez-teal" strokeWidth={1.5} />
                </div>
              )}
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={22} className="text-ez-teal shrink-0" />
                  <h3 className="font-outfit text-xl font-bold text-ez-text">{title}</h3>
                </div>
                <p className="text-ez-muted font-jakarta leading-relaxed mb-6 flex-1">{text}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={primary.href}
                    {...(primary.external ? externalProps : {})}
                    className="ez-btn-primary text-sm py-3 px-6 inline-flex items-center justify-center gap-2"
                  >
                    {primary.label}
                    {primary.external && <ExternalLink size={16} />}
                  </a>
                  {secondary && (
                    <a
                      href={secondary.href}
                      {...(secondary.external ? externalProps : {})}
                      className="flex items-center justify-center gap-2 border border-ez-teal text-ez-teal hover:bg-ez-teal hover:text-white transition-all duration-200 py-3 px-6 rounded-full text-sm font-semibold"
                    >
                      {secondary.label}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
